/// Forecast reading + isobar interpretation — Day Skipper theory
/// extras beyond the basic terms catalogue.
///
/// Two domains:
///
///   1. Wind from isobars — geostrophic + gradient wind + isobar
///      spacing rule-of-thumb.
///   2. Shipping Forecast structure — what each part of the
///      broadcast contains + how to transcribe it.

// ───────────────────────────────────────────────────────────────────
// 1. Wind from isobars
// ───────────────────────────────────────────────────────────────────

export interface IsobarWindRule {
  id: "geostrophic" | "gradient" | "spacing-rule" | "buys-ballot-reminder";
  name: string;
  /// One-line gist.
  summary: string;
  /// Detailed explanation.
  detail: string;
}

export const ISOBAR_WIND_RULES: readonly IsobarWindRule[] = Object.freeze([
  {
    id: "geostrophic",
    name: "Geostrophic wind",
    summary:
      "Wind blows PARALLEL to the isobars when only Coriolis + pressure gradient act on it. Low pressure on the LEFT in N hemisphere.",
    detail:
      "Theoretical wind in the upper atmosphere where surface friction is negligible. In real life, the surface wind is slowed + deflected toward low pressure by ~15° over sea + ~30° over land — so 'real' wind crosses the isobars at this angle.",
  },
  {
    id: "gradient",
    name: "Gradient wind",
    summary:
      "Wind that follows CURVED isobars round a low or high. Stronger round a high, weaker round a low (compared to geostrophic).",
    detail:
      "When isobars curve, the centripetal force changes the force balance. Round a LOW, centripetal force adds to Coriolis + opposes pressure gradient — net effect: wind weaker than geostrophic. Round a HIGH, centripetal force opposes Coriolis — wind stronger than geostrophic.",
  },
  {
    id: "spacing-rule",
    name: "Isobar spacing → wind strength",
    summary:
      "Tightly-packed isobars = strong wind. Widely-spaced isobars = light wind.",
    detail:
      "Standard isobar spacing on a synoptic chart is 4 mb (millibars / hectopascals). UK rules of thumb (at 50°N + standard 4 mb isobar spacing): ~250 nm between isobars = ~5 kt; ~125 nm = ~10 kt; ~60 nm = ~20 kt; ~30 nm = ~40 kt; ~15 nm = ~80 kt+. Strength roughly DOUBLES each time the spacing HALVES.",
  },
  {
    id: "buys-ballot-reminder",
    name: "Buys Ballot's Law — direction from wind",
    summary:
      "Stand with your back to the wind. Low pressure is on your LEFT in N hemisphere; RIGHT in S hemisphere.",
    detail:
      "Combined with isobar spacing, this gives a quick mental synoptic chart: feel the wind, locate the low. Watch the wind shift over hours to track the low's movement (backing = low approaching; veering = low past).",
  },
]);

// ───────────────────────────────────────────────────────────────────
// 2. Shipping Forecast structure
// ───────────────────────────────────────────────────────────────────

export interface ShippingForecastSection {
  id: string;
  /// Section name as broadcast.
  name: string;
  /// What this section contains.
  contains: string;
  /// What to LISTEN for + capture in your notes.
  listenFor: string;
}

export const SHIPPING_FORECAST_STRUCTURE: readonly ShippingForecastSection[] = Object.freeze([
  {
    id: "headline",
    name: "Headline",
    contains:
      "Date + time of issue + 'The Shipping Forecast issued by the Met Office' OR the inshore equivalent.",
    listenFor:
      "Timing — typically four times a day on UK long wave. Note when the forecast was issued; freshness matters.",
  },
  {
    id: "warnings",
    name: "Gale + storm warnings in force",
    contains:
      "Areas where gale (F8+) or storm (F10+) warnings are currently in force.",
    listenFor:
      "Whether YOUR area is named. If named, expect F8+ within the next 24 hours.",
  },
  {
    id: "general-synopsis",
    name: "General synopsis",
    contains:
      "Where the depressions + highs are, their pressures, and how they're moving over the forecast period.",
    listenFor:
      "Position of depressions + highs; movement direction + speed. Lets you mentally place the systems before the area-by-area detail.",
  },
  {
    id: "sea-areas",
    name: "Sea-area forecasts",
    contains:
      "Each of the 31 UK sea areas in alphabetical order: WIND (direction + force + change), SEA STATE, WEATHER, VISIBILITY.",
    listenFor:
      "WIND: e.g. 'Southwest 5 to 7, perhaps gale 8 later'. SEA STATE: 'rough or very rough'. WEATHER: 'rain, drizzle'. VISIBILITY: 'good, occasionally moderate or poor'.",
  },
  {
    id: "coastal-station-reports",
    name: "Coastal station reports",
    contains:
      "Reports from named coastal stations — actual wind + sea + visibility OBSERVED at that station now (not a forecast).",
    listenFor:
      "Reality check against the forecast. If a coastal station already shows F8 when the forecast says F6, the system is ahead of schedule.",
  },
]);

// ───────────────────────────────────────────────────────────────────
// Shipping Forecast vocabulary reminders — to support transcription
// ───────────────────────────────────────────────────────────────────

export const TRANSCRIPTION_TIPS: readonly string[] = Object.freeze([
  "Use Met Office abbreviations: SW = southwesterly; F8 = Force 8; 'rough' = sea state 5 (4-6 m waves).",
  "Time qualifiers: 'imminent' = within 6 h; 'soon' = 6-12 h; 'later' = 12-24 h; 'slowly/quickly' rate of change.",
  "WIND direction = the direction the wind is FROM (not to). 'SW 7' = wind from south-west at Force 7.",
  "Numerical wind forces use the Beaufort scale; descriptions ('strong', 'gale') are at fixed breakpoints in the same scale.",
  "When the broadcast says 'becoming SW 6 later', annotate: 'now S 7 → SW 6 by 18 h forecast time'.",
  "If a sea area is omitted from a Coastal Skipper-level forecast, that means no significant change from the previous report (very rare).",
]);
